import { useQuery } from "@tanstack/react-query";

import type { Task } from "@recobra/schemas/task";
import type { TaskType } from "@recobra/schemas/task-catalog";

import { casesApi } from "../../lib/api";
import { useTaskTypes } from "../task-data";

/** The task's type, which carries its flow, plus the parent case where the task shows it. */
export interface TaskRequirementContext {
  /** The parent case, read only by a liability analysis; null otherwise or until it loads. */
  parent: Record<string, unknown> | null;
  /** The task's type; undefined while loading or once it no longer exists. */
  taskType: TaskType | undefined;
  /** The task's type no longer exists, so the task can only be canceled. */
  unconfigured: boolean;
  isLoading: boolean;
  /** A failed context read; the task type cannot be told apart from a missing one. */
  error: Error | null;
}

/**
 * The task's type and, for a liability analysis, its case for the reviewed values it shows.
 * The query key matches the route prefetch so nothing is fetched twice.
 */
export const useTaskRequirementContext = (
  task: Task | null | undefined,
): TaskRequirementContext => {
  const { contextType, contextCaseNumber, type = "", system } = task ?? {};
  const caseNumber = contextType === "case" ? contextCaseNumber : null;
  const types = useTaskTypes();
  const caseQuery = useQuery({
    queryKey: ["cases", caseNumber],
    queryFn: () => casesApi.getCase(caseNumber!),
    enabled: system === "analyze_case_liability" && caseNumber != null,
  });

  return {
    parent: (caseQuery.data ?? null) as Record<string, unknown> | null,
    taskType: types.data?.get(type),
    unconfigured: types.isSuccess && !types.data.has(type),
    isLoading: types.isLoading || caseQuery.isLoading,
    error: types.error ?? caseQuery.error,
  };
};
